// Shrink-to-fit calculator for text elements whose layout has been committed
// or contained. The element's width and height are treated as the hard frame;
// if the current text at the current fontSize overflows, we search for the
// largest fontSize that fits. We never grow above the authored fontSize.

import { measureTextBlockHeight } from './textMetrics';

export interface FitInput {
  text: string;
  boxWidth: number;
  boxHeight: number;
  fontSize: number;
  fontFamily: string;
  fontWeight?: number | string;
  fontStyle?: string;
  lineHeight?: number;
  letterSpacing?: number;
  minFontSize?: number;
}

export interface FitResult {
  fittedFontSize: number;
  shrunk: boolean;
  lines: string[];
  /**
   * True when the content still exceeds the box even at minFontSize (the
   * 8px floor). Consumed by the Spec §7 export-blocking validation gate.
   * The verdict comes from this fitter's own measurement — single
   * adjudicator, no cross-system reconciliation (per the locked
   * single-measurement-authority decision).
   */
  overflow: boolean;
}

let sharedCtx: CanvasRenderingContext2D | null = null;
function getCtx(): CanvasRenderingContext2D | null {
  if (sharedCtx) return sharedCtx;
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  sharedCtx = c.getContext('2d');
  return sharedCtx;
}

function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  boxWidth: number,
  letterSpacing: number
): string[] {
  const paragraphs = (text || '').split('\n');
  const out: string[] = [];
  const extraPerChar = letterSpacing || 0;

  for (const para of paragraphs) {
    if (!para) {
      out.push('');
      continue;
    }
    const words = para.split(/(\s+)/);
    let line = '';
    for (const token of words) {
      const candidate = line + token;
      const w = ctx.measureText(candidate).width + extraPerChar * Math.max(0, candidate.length - 1);
      if (w <= boxWidth || line.length === 0) {
        line = candidate;
      } else {
        out.push(line.replace(/\s+$/, ''));
        line = token.replace(/^\s+/, '');
      }
    }
    if (line) out.push(line);
  }
  return out.length ? out : [''];
}

export function computeFittedFontSize(input: FitInput): FitResult {
  const {
    text,
    boxWidth,
    boxHeight,
    fontSize,
    fontFamily,
    fontWeight = 400,
    fontStyle = 'normal',
    lineHeight = 1.16,
    letterSpacing = 0,
    minFontSize = 8,
  } = input;

  if (!(boxWidth > 0) || !(boxHeight > 0) || !(fontSize > 0)) {
    return { fittedFontSize: fontSize, shrunk: false, lines: (text || '').split('\n'), overflow: false };
  }

  const ctx = getCtx();
  if (!ctx) {
    return { fittedFontSize: fontSize, shrunk: false, lines: (text || '').split('\n'), overflow: false };
  }

  const tryFit = (size: number): { ok: boolean; lines: string[] } => {
    try {
      ctx.font = `${fontStyle} ${fontWeight} ${size}px "${fontFamily}"`;
    } catch {
      return { ok: true, lines: (text || '').split('\n') };
    }
    const lines = wrapLines(ctx, text, boxWidth, letterSpacing);
    const { height } = measureTextBlockHeight({
      lines,
      fontSize: size,
      fontFamily,
      fontWeight,
      fontStyle,
      lineHeight,
    });
    const maxLineWidth = lines.reduce((m, l) => {
      const w = ctx.measureText(l).width + letterSpacing * Math.max(0, l.length - 1);
      return Math.max(m, w);
    }, 0);
    return { ok: height <= boxHeight && maxLineWidth <= boxWidth, lines };
  };

  const initial = tryFit(fontSize);
  if (initial.ok) {
    return { fittedFontSize: fontSize, shrunk: false, lines: initial.lines, overflow: false };
  }

  let lo = minFontSize;
  let hi = fontSize;
  let fitFound = false;
  let best = { size: minFontSize, lines: initial.lines };
  for (let i = 0; i < 18; i++) {
    const mid = (lo + hi) / 2;
    const attempt = tryFit(mid);
    if (attempt.ok) {
      best = { size: mid, lines: attempt.lines };
      fitFound = true;
      lo = mid;
    } else {
      hi = mid;
    }
    if (hi - lo < 0.25) break;
  }

  // Definitive floor probe. The binary search's mid is always > lo, so it
  // never tests minFontSize exactly. If no mid fit, the floor itself is the
  // last candidate: probe it once. This both closes the "fits at exactly
  // 8px" edge case and produces correctly-wrapped lines for the clamped
  // path (previously `best.lines` kept the original-size wrap when nothing
  // fit — wrong wrapping for every consumer of `lines`).
  if (!fitFound) {
    const atFloor = tryFit(minFontSize);
    best = { size: minFontSize, lines: atFloor.lines };
    fitFound = atFloor.ok;
  }

  const rounded = Math.max(minFontSize, Math.floor(best.size * 10) / 10);
  return {
    fittedFontSize: rounded,
    shrunk: rounded < fontSize,
    lines: best.lines,
    overflow: !fitFound,
  };
}
